import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  BarChart3, Calendar, Clock, Download, FileText, Mail, Eye,
  ChevronRight, ChevronDown, Search, Package, Users, Truck, Warehouse,
  Activity, AlertTriangle, BarChart, TrendingUp, Settings, Menu, X
} from 'lucide-react';
import {
  BarChart as ReBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend
} from 'recharts';
import * as XLSX from 'xlsx';

const REPORT_DEFINITIONS = [
  { id: 'daily_movement_summary', name: 'Daily Movement Summary', frequency: 'daily', icon: Activity, endpoint: '/reports/daily/movement-summary', param: 'date', roleMin: 'user' },
  { id: 'daily_user_activity', name: 'Daily User Activity', frequency: 'daily', icon: Users, endpoint: '/reports/daily/user-activity', param: 'date', roleMin: 'manager' },
  { id: 'daily_dispatch_summary', name: 'Daily Dispatch Summary', frequency: 'daily', icon: Truck, endpoint: '/reports/daily/dispatch-summary', param: 'date', roleMin: 'user' },
  { id: 'weekly_inventory_flow', name: 'Weekly Inventory Flow', frequency: 'weekly', icon: TrendingUp, endpoint: '/reports/weekly/inventory-flow', param: 'start_date', roleMin: 'manager' },
  { id: 'weekly_space_utilisation', name: 'Weekly Space Utilisation', frequency: 'weekly', icon: Warehouse, endpoint: '/reports/weekly/space-utilisation', param: 'start_date', roleMin: 'manager' },
  { id: 'weekly_user_productivity', name: 'Weekly User Productivity', frequency: 'weekly', icon: BarChart3, endpoint: '/reports/weekly/user-productivity', param: 'start_date', roleMin: 'manager' },
  { id: 'monthly_inventory_ageing', name: 'Monthly Inventory Ageing', frequency: 'monthly', icon: Clock, endpoint: '/reports/monthly/inventory-ageing', param: 'month', roleMin: 'manager' },
  { id: 'monthly_customer_dispatch', name: 'Monthly Customer Dispatch', frequency: 'monthly', icon: Package, endpoint: '/reports/monthly/customer-dispatch', param: 'month', roleMin: 'manager' },
  { id: 'monthly_system_usage', name: 'Monthly System Usage', frequency: 'monthly', icon: BarChart, endpoint: '/reports/monthly/system-usage', param: 'month', roleMin: 'admin' },
  { id: 'quarterly_stock_movement', name: 'Quarterly Stock Movement', frequency: 'quarterly', icon: TrendingUp, endpoint: '/reports/quarterly/stock-movement', param: 'quarter', roleMin: 'manager' },
  { id: 'stock_on_hand', name: 'Stock-On-Hand (Total)', frequency: 'on-demand', icon: Package, endpoint: '/reports/ondemand/stock-on-hand', param: null, roleMin: 'user', hasStockFilters: true },
  { id: 'customer_stock', name: 'Customer Stock Report', frequency: 'on-demand', icon: Package, endpoint: '/reports/ondemand/customer-stock', param: 'customer_name', roleMin: 'user' },
  { id: 'slow_moving', name: 'Slow-Moving Stock', frequency: 'on-demand', icon: AlertTriangle, endpoint: '/reports/ondemand/slow-moving', param: 'days_threshold', roleMin: 'user' },
  { id: 'batch_traceability', name: 'Batch/Heat Traceability', frequency: 'on-demand', icon: Search, endpoint: '/reports/ondemand/batch-traceability', param: 'heat_no', roleMin: 'user' },
  { id: 'space_recovery', name: 'Space Recovery Report', frequency: 'on-demand', icon: Warehouse, endpoint: '/reports/ondemand/space-recovery', param: null, roleMin: 'manager' },
];

const FREQ_COLORS = {
  daily: 'bg-blue-100 text-blue-700',
  weekly: 'bg-green-100 text-green-700',
  monthly: 'bg-purple-100 text-purple-700',
  quarterly: 'bg-amber-100 text-amber-700',
  'on-demand': 'bg-gray-100 text-gray-700',
};

const FREQ_ORDER = ['daily', 'weekly', 'monthly', 'quarterly', 'on-demand'];
const AGEING_COLORS = { '0-30': '#22c55e', '31-60': '#84cc16', '61-90': '#f59e0b', '91-120': '#f97316', '120+': '#ef4444' };
const CATEGORY_COLORS = { Bolt: '#1a56db', Nut: '#7e3af2', Washer: '#0891b2', Stud: '#d97706', Screw: '#059669', Rod: '#dc2626', Pin: '#8b5cf6', Other: '#6b7280', other: '#6b7280' };

function formatValue(val) {
  if (val === null || val === undefined) return '\u2014';
  if (typeof val === 'number') return val.toLocaleString();
  if (typeof val === 'object' && !Array.isArray(val)) return JSON.stringify(val);
  return String(val);
}

function DataTable({ data, title }) {
  if (!data || (Array.isArray(data) && data.length === 0)) return (
    <div className="py-4 text-center" data-testid={`table-${(title || '').replace(/\s/g, '-').toLowerCase()}-empty`}>
      {title && <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>}
      <p className="text-sm text-gray-400">No data available for this period yet. Data will appear as activity is recorded.</p>
    </div>
  );
  if (!Array.isArray(data)) return null;
  const keys = Object.keys(data[0] || {}).filter(k => !['items', 'external_issue_details', 'items_dispatched'].includes(k));
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      {title && <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>}
      <table className="w-full text-xs md:text-sm border-collapse min-w-[500px]" data-testid={`table-${(title || '').replace(/\s/g, '-').toLowerCase()}`}>
        <thead>
          <tr className="bg-[#0a1628] text-white">
            {keys.map(k => <th key={k} className="px-2 md:px-3 py-2 text-left font-medium whitespace-nowrap text-[11px] md:text-xs">{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.id || row.pallet_ref || row.slot || row.item_code || `row-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {keys.map(k => <td key={k} className="px-2 md:px-3 py-1.5 border-b border-gray-100 whitespace-nowrap text-[11px] md:text-xs">{formatValue(row[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetricCards({ data }) {
  if (!data || typeof data !== 'object') return null;
  const entries = Object.entries(data).filter(([, v]) => typeof v !== 'object' || v === null);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
      {entries.map(([k, v]) => (
        <div key={k} className="bg-white border border-gray-100 rounded-lg p-2 md:p-3 shadow-sm" data-testid={`metric-${k}`}>
          <div className="text-[9px] md:text-[10px] text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
          <div className="text-base md:text-xl font-bold text-gray-900 mt-0.5">{formatValue(v)}</div>
        </div>
      ))}
    </div>
  );
}

function SpaceUtilChart({ data }) {
  if (!data?.top_10_space_blockers?.length) return (
    <div className="bg-white rounded-lg p-4 border text-center text-sm text-gray-400" data-testid="chart-space-util-empty">
      No space blocker data available for this period yet. Data will appear as activity is recorded.
    </div>
  );
  const chartData = data.top_10_space_blockers.map(b => ({ name: b.pallet_ref, score: b.space_score, days: b.days }));
  return (
    <div className="bg-white rounded-lg p-3 md:p-4 border" data-testid="chart-space-util">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Space Blockers (Space Score = Days x Cartons)</h4>
      <ResponsiveContainer width="100%" height={280}>
        <ReBarChart data={chartData} layout="vertical" margin={{ left: 60, right: 20, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={60} />
          <Tooltip formatter={(v) => [v.toLocaleString(), 'Space Score']} />
          <Bar dataKey="score" fill="#1a2fce" radius={[0, 4, 4, 0]} />
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SystemUsageChart({ data }) {
  const hasData = data?.peak_usage_hours?.some(h => h.actions > 0);
  if (!data?.peak_usage_hours?.length || !hasData) return (
    <div className="bg-white rounded-lg p-4 border text-center text-sm text-gray-400" data-testid="chart-system-usage-empty">
      No usage data available for this period yet. Data will appear as activity is recorded.
    </div>
  );
  return (
    <div className="bg-white rounded-lg p-3 md:p-4 border" data-testid="chart-system-usage">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Actions by Hour of Day</h4>
      <ResponsiveContainer width="100%" height={280}>
        <ReBarChart data={data.peak_usage_hours} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" tick={{ fontSize: 11 }} label={{ value: 'Hour', position: 'insideBottom', offset: -2, fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => [v, 'Actions']} />
          <Bar dataKey="actions" fill="#0F6E56" radius={[4, 4, 0, 0]} />
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
}

function AgeingChart({ data }) {
  const hasData = data?.summary && Object.values(data.summary).some(v => v > 0);
  if (!data?.summary || !hasData) return (
    <div className="bg-white rounded-lg p-4 border text-center text-sm text-gray-400" data-testid="chart-ageing-empty">
      No ageing data available for this period yet. Data will appear as activity is recorded.
    </div>
  );
  const chartData = Object.entries(data.summary).map(([band, count]) => ({ band, count, fill: AGEING_COLORS[band] || '#6b7280' }));
  return (
    <div className="bg-white rounded-lg p-3 md:p-4 border" data-testid="chart-ageing">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Pallets by Age Band</h4>
      <ResponsiveContainer width="100%" height={280}>
        <ReBarChart data={chartData} layout="vertical" margin={{ left: 50, right: 20, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="band" type="category" tick={{ fontSize: 12 }} width={50} />
          <Tooltip formatter={(v) => [v, 'Pallets']} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {chartData.map((entry) => <Cell key={entry.band} fill={entry.fill} />)}
          </Bar>
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
}

function InventoryFlowChart({ data }) {
  if (!data?.category_breakdown || Object.keys(data.category_breakdown).length === 0) return (
    <div className="bg-white rounded-lg p-4 border text-center text-sm text-gray-400" data-testid="chart-inventory-flow-empty">
      No inventory flow data available for this period yet. Data will appear as activity is recorded.
    </div>
  );
  const chartData = Object.entries(data.category_breakdown).map(([cat, vals]) => ({
    category: cat.charAt(0).toUpperCase() + cat.slice(1),
    pcs: vals.pcs,
    kg: vals.kg,
    fill: CATEGORY_COLORS[cat] || CATEGORY_COLORS['Other'],
  }));
  if (chartData.length === 0) return null;
  return (
    <div className="bg-white rounded-lg p-3 md:p-4 border" data-testid="chart-inventory-flow">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Inventory by Category</h4>
      <ResponsiveContainer width="100%" height={300}>
        <ReBarChart data={chartData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="category" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="pcs" name="Pieces" radius={[4, 4, 0, 0]}>
            {chartData.map((entry) => <Cell key={entry.category} fill={entry.fill} />)}
          </Bar>
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
}

function StockOnHandRenderer({ data }) {
  if (!data || !data.rows) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-3">
      {/* Totals summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="stock-on-hand-totals">
        {[
          { label: 'Pallets', value: t.total_pallets, color: '#1a2fce' },
          { label: 'Line Items', value: t.total_rows, color: '#0d1f6e' },
          { label: 'Customers', value: t.total_customers, color: '#7c3aed' },
          { label: 'Pieces', value: (t.total_pcs || 0).toLocaleString(), color: '#0e7a45' },
          { label: 'Cartons', value: t.total_ctns, color: '#d97706' },
          { label: 'Net Wt (kg)', value: (t.total_weight_net_kg || 0).toLocaleString(), color: '#dc2626' },
          { label: 'Gross Wt (kg)', value: (t.total_weight_gross_kg || 0).toLocaleString(), color: '#6b7280' },
        ].map((x) => (
          <div key={x.label} className="border rounded-lg p-2 bg-white shadow-sm">
            <div className="text-[10px] uppercase text-gray-500">{x.label}</div>
            <div className="text-base font-bold mt-0.5" style={{ color: x.color }}>{x.value ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Grouped summary (if group_by used) */}
      {Array.isArray(data.groups) && data.groups.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase">
            Grouped by {data.filters?.group_by?.replace(/_/g, ' ')} ({data.groups.length})
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-[10px] uppercase text-gray-500">
                  <th className="px-2 py-1.5">{data.filters?.group_by?.replace(/_/g, ' ')}</th>
                  <th className="px-2 py-1.5 text-right">Pallets</th>
                  <th className="px-2 py-1.5 text-right">Lines</th>
                  <th className="px-2 py-1.5 text-right">Pcs</th>
                  <th className="px-2 py-1.5 text-right">Ctns</th>
                  <th className="px-2 py-1.5 text-right">Net kg</th>
                  <th className="px-2 py-1.5 text-right">Gross kg</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.map((g, i) => (
                  <tr key={`${g[data.filters.group_by]}-${i}`} className="border-t hover:bg-gray-50">
                    <td className="px-2 py-1 font-medium">{g[data.filters.group_by] || '(blank)'}</td>
                    <td className="px-2 py-1 text-right">{g.pallet_count}</td>
                    <td className="px-2 py-1 text-right">{g.line_count}</td>
                    <td className="px-2 py-1 text-right">{(g.qty_pcs || 0).toLocaleString()}</td>
                    <td className="px-2 py-1 text-right">{g.ctns_count}</td>
                    <td className="px-2 py-1 text-right">{(g.weight_net_kg || 0).toLocaleString()}</td>
                    <td className="px-2 py-1 text-right">{(g.weight_gross_kg || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail rows */}
      <div className="border rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase flex items-center justify-between">
          <span>Detail ({data.rows.length} lines)</span>
          {data.rows.length > 500 && (
            <span className="text-[10px] text-gray-500 font-normal">(showing first 500 — download for full export)</span>
          )}
        </div>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-[10px] uppercase text-gray-500">
                <th className="px-2 py-1.5">Pallet</th>
                <th className="px-2 py-1.5">Slot</th>
                <th className="px-2 py-1.5">Customer</th>
                <th className="px-2 py-1.5">Item</th>
                <th className="px-2 py-1.5">Description</th>
                <th className="px-2 py-1.5">Category</th>
                <th className="px-2 py-1.5">Batch</th>
                <th className="px-2 py-1.5 text-right">Pcs</th>
                <th className="px-2 py-1.5 text-right">Ctns</th>
                <th className="px-2 py-1.5 text-right">Net kg</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.slice(0, 500).map((r, i) => (
                <tr key={`${r.pallet_ref}-${r.item_code}-${i}`} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-1 font-mono">{r.pallet_ref}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.slot_address}</td>
                  <td className="px-2 py-1">{r.customer_name}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.item_code}</td>
                  <td className="px-2 py-1 truncate max-w-[240px]">{r.description}</td>
                  <td className="px-2 py-1">{r.category}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.batch_no}</td>
                  <td className="px-2 py-1 text-right">{r.qty_pcs.toLocaleString()}</td>
                  <td className="px-2 py-1 text-right">{r.ctns_count}</td>
                  <td className="px-2 py-1 text-right">{r.weight_net_kg.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportRenderer({ data, reportDef }) {
  if (!data) return null;
  const metrics = {};
  const tables = {};
  for (const [k, v] of Object.entries(data)) {
    if (Array.isArray(v)) {
      tables[k] = v;
    } else if (v && typeof v === 'object') {
      for (const [sk, sv] of Object.entries(v)) {
        metrics[`${k}_${sk}`] = sv;
      }
    } else {
      metrics[k] = v;
    }
  }
  return (
    <div className="space-y-4">
      {reportDef.id !== 'stock_on_hand' && <MetricCards data={metrics} />}
      {reportDef.id === 'weekly_space_utilisation' && <SpaceUtilChart data={data} />}
      {reportDef.id === 'monthly_system_usage' && <SystemUsageChart data={data} />}
      {reportDef.id === 'monthly_inventory_ageing' && <AgeingChart data={data} />}
      {reportDef.id === 'weekly_inventory_flow' && <InventoryFlowChart data={data} />}
      {reportDef.id === 'stock_on_hand' && <StockOnHandRenderer data={data} />}
      {reportDef.id !== 'stock_on_hand' && Object.entries(tables).map(([key, rows]) => (
        <DataTable key={key} data={rows} title={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateParam, setDateParam] = useState('');
  const [extraParam, setExtraParam] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [ccEmails, setCcEmails] = useState(['', '', '']);
  const [enabledReports, setEnabledReports] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Stock-On-Hand filters
  const [stockFilters, setStockFilters] = useState({
    customer_name: '', category: '', item_code: '', finish: '',
    group_by: '', include_qc_hold: true, include_partial: true, include_ready: false,
  });
  const [customers, setCustomers] = useState([]);

  const userStr = localStorage.getItem('wms_user');
  const user = userStr ? JSON.parse(userStr) : {};
  const userRole = user.role || 'user';

  const visibleReports = useMemo(() => {
    const roleLevel = { user: 0, manager: 1, admin: 2 };
    const uLevel = roleLevel[userRole] || 0;
    return REPORT_DEFINITIONS.filter(r => {
      const rLevel = roleLevel[r.roleMin] || 0;
      if (uLevel === 0) return r.id === 'daily_movement_summary';
      return uLevel >= rLevel;
    });
  }, [userRole]);

  const groupedReports = useMemo(() => {
    const groups = {};
    for (const r of visibleReports) {
      if (!groups[r.frequency]) groups[r.frequency] = [];
      groups[r.frequency].push(r);
    }
    return groups;
  }, [visibleReports]);

  useEffect(() => {
    if (userRole === 'admin') {
      api.get('/reports/settings').then(r => {
        setCcEmails(r.data.cc_emails?.length ? [...r.data.cc_emails, '', '', ''].slice(0, 3) : ['', '', '']);
        setEnabledReports(r.data.enabled_reports || {});
      }).catch((err) => { console.error('Failed to load report settings:', err); });
    }
  }, [userRole]);

  const todayStr = new Date().toISOString().split('T')[0];
  const thisMonthStr = new Date().toISOString().slice(0, 7);

  const fetchReport = useCallback(async (reportDef) => {
    setLoading(true);
    setReportData(null);
    try {
      const params = {};
      if (reportDef.param && dateParam) params[reportDef.param] = dateParam;
      if (reportDef.id === 'slow_moving' && extraParam) params.days_threshold = parseInt(extraParam) || 60;
      if (reportDef.id === 'batch_traceability' && extraParam) params.heat_no = extraParam;
      if (reportDef.id === 'customer_stock' && extraParam) params.customer_name = extraParam;
      if (reportDef.hasStockFilters) {
        Object.entries(stockFilters).forEach(([k, v]) => {
          if (v === '' || v === null || v === undefined) return;
          params[k] = v;
        });
      }
      const res = await api.get(reportDef.endpoint, { params });
      setReportData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateParam, extraParam, stockFilters]);

  // Direct download from backend (Excel/CSV) — used for Stock-On-Hand
  const handleServerDownload = useCallback(async (fmt = 'xlsx') => {
    if (!selectedReport) return;
    try {
      const params = { fmt };
      Object.entries(stockFilters).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        params[k] = v;
      });
      const res = await api.get(selectedReport.endpoint, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = fmt === 'xlsx' ? 'xlsx' : 'csv';
      const ts = new Date().toISOString().replace(/[:T]/g, '').slice(0, 15);
      a.download = `stock_on_hand_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  }, [selectedReport, stockFilters]);

  // Load customers for filter dropdown (only when needed)
  useEffect(() => {
    if (selectedReport?.hasStockFilters && customers.length === 0) {
      api.get('/customers').then(r => {
        setCustomers(Array.isArray(r.data) ? r.data : (r.data.customers || []));
      }).catch(() => {});
    }
  }, [selectedReport, customers.length]);

  const handleExportExcel = useCallback(() => {
    if (!reportData || !selectedReport) return;
    try {
      const wb = XLSX.utils.book_new();

      // Title sheet info row
      const headerRows = [
        ['Singhania International Ltd.'],
        [`Report: ${selectedReport.name}`],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
      ];

      for (const [key, value] of Object.entries(reportData)) {
        if (Array.isArray(value) && value.length > 0) {
          const sheetData = [...headerRows, Object.keys(value[0]), ...value.map(r => Object.values(r))];
          const ws = XLSX.utils.aoa_to_sheet(sheetData);
          // Navy header style — column widths
          ws['!cols'] = Object.keys(value[0]).map(() => ({ wch: 18 }));
          XLSX.utils.book_append_sheet(wb, ws, key.replace(/_/g, ' ').slice(0, 31));
        }
      }

      // Summary metrics sheet
      const metricRows = [...headerRows];
      for (const [key, value] of Object.entries(reportData)) {
        if (!Array.isArray(value) && typeof value !== 'object') {
          metricRows.push([key.replace(/_/g, ' '), value]);
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          for (const [sk, sv] of Object.entries(value)) {
            metricRows.push([`${key} - ${sk}`.replace(/_/g, ' '), sv]);
          }
        }
      }
      if (metricRows.length > headerRows.length) {
        const ws = XLSX.utils.aoa_to_sheet(metricRows);
        ws['!cols'] = [{ wch: 35 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Summary');
      }

      XLSX.writeFile(wb, `${selectedReport.name.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Report exported as Excel');
    } catch {
      // Fallback to CSV
      let csv = '';
      for (const [key, value] of Object.entries(reportData)) {
        if (Array.isArray(value) && value.length > 0) {
          csv += `\n${key}\n`;
          const headers = Object.keys(value[0]);
          csv += headers.join(',') + '\n';
          for (const row of value) {
            csv += headers.map(h => { const v = row[h]; return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? ''; }).join(',') + '\n';
          }
        }
      }
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport.name.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.info('Excel unavailable \u2014 exported as CSV fallback');
    }
  }, [reportData, selectedReport]);

  const handleEmailNow = useCallback(async () => {
    if (!selectedReport) return;
    try {
      await api.post(`/reports/email-now/${selectedReport.id}`);
      toast.success('Report email queued');
    } catch (err) { toast.error('Failed to send email'); console.error(err); }
  }, [selectedReport]);

  const saveSettings = useCallback(async () => {
    try {
      await api.post('/reports/settings', { cc_emails: ccEmails.filter(e => e.trim()), enabled_reports: enabledReports });
      toast.success('Report settings saved');
      setShowSettings(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save settings'); }
  }, [ccEmails, enabledReports]);

  const selectReport = (r) => {
    setSelectedReport(r);
    setReportData(null);
    setDateParam('');
    setExtraParam('');
    setMobileMenuOpen(false);
  };

  const SidebarContent = () => (
    <>
      {FREQ_ORDER.map(freq => {
        const reports = groupedReports[freq];
        if (!reports?.length) return null;
        return (
          <div key={freq} className="py-1.5">
            <div className="px-3 py-1"><Badge className={`text-[9px] ${FREQ_COLORS[freq]}`}>{freq.toUpperCase()}</Badge></div>
            {reports.map(r => {
              const Icon = r.icon;
              const isSelected = selectedReport?.id === r.id;
              return (
                <button key={r.id} onClick={() => selectReport(r)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 text-left text-sm transition-colors ${
                    isSelected ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600' : 'text-gray-700 hover:bg-gray-50'
                  }`} data-testid={`report-nav-${r.id}`}>
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate text-[14px] md:text-sm">{r.name}</span>
                  <ChevronRight className="h-3 w-3 ml-auto flex-shrink-0 opacity-40" />
                </button>
              );
            })}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-64px)]" data-testid="reports-page">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-72 border-r bg-white overflow-y-auto flex-shrink-0">
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Reports</h3>
          {userRole === 'admin' && (
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)} data-testid="reports-settings-btn">
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
        <SidebarContent />
      </div>

      {/* Mobile Header + Dropdown */}
      <div className="md:hidden border-b bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-gray-900">Reports</h3>
          <div className="flex gap-2">
            {userRole === 'admin' && (
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)} data-testid="reports-settings-btn-mobile">
                <Settings className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full flex items-center justify-between p-3 border rounded-lg bg-gray-50 text-sm"
          data-testid="mobile-report-selector">
          <span className="flex items-center gap-2">
            {selectedReport ? (
              <><selectedReport.icon className="h-4 w-4 text-[#1a2fce]" /> {selectedReport.name}</>
            ) : (
              <><FileText className="h-4 w-4 text-gray-400" /> Select a report</>
            )}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileMenuOpen && (
          <div className="mt-2 border rounded-lg bg-white shadow-lg max-h-[50vh] overflow-y-auto" data-testid="mobile-report-list">
            <SidebarContent />
          </div>
        )}
      </div>

      {/* Right Panel */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-3 md:p-4">
        {/* Settings Panel */}
        {showSettings && userRole === 'admin' && (
          <Card className="mb-4" data-testid="reports-settings-panel">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Report Email Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-medium">CC Email Addresses (max 3)</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1">
                  {ccEmails.map((email, i) => (
                    <Input key={i} type="email" value={email} placeholder={`CC email ${i + 1}`}
                      onChange={e => { const arr = [...ccEmails]; arr[i] = e.target.value; setCcEmails(arr); }}
                      className="h-9 text-sm" data-testid={`cc-email-${i}`} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium mb-2 block">Enable/Disable Auto-Email Reports</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {REPORT_DEFINITIONS.filter(r => r.frequency !== 'on-demand').map(r => (
                    <div key={r.id} className="flex items-center gap-2">
                      <Switch checked={enabledReports[r.id] !== false}
                        onCheckedChange={checked => setEnabledReports(prev => ({ ...prev, [r.id]: checked }))}
                        data-testid={`toggle-${r.id}`} />
                      <span className="text-xs md:text-sm text-gray-700">{r.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Button size="sm" onClick={saveSettings} data-testid="save-report-settings">Save Settings</Button>
            </CardContent>
          </Card>
        )}

        {!selectedReport ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
            <FileText className="h-12 md:h-16 w-12 md:w-16 mb-4 opacity-30" />
            <p className="text-base md:text-lg font-medium">Select a report</p>
            <p className="text-sm mt-1 text-center">Choose a report type to view, export, or email</p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {/* Report Header */}
            <Card data-testid="report-header-card">
              <CardContent className="pt-4 pb-3">
                <div className="space-y-3">
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-gray-900" data-testid="report-title">{selectedReport.name}</h2>
                    <Badge className={`text-[9px] mt-1 ${FREQ_COLORS[selectedReport.frequency]}`}>{selectedReport.frequency.toUpperCase()}</Badge>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
                    {/* Date/Parameter Inputs */}
                    {selectedReport.param === 'date' && (
                      <div className="flex-1">
                        <Label className="text-[10px] md:text-xs">Date</Label>
                        <Input type="date" value={dateParam} onChange={e => setDateParam(e.target.value)} max={todayStr} className="h-9 text-sm w-full" data-testid="report-date-input" />
                      </div>
                    )}
                    {selectedReport.param === 'start_date' && (
                      <div className="flex-1">
                        <Label className="text-[10px] md:text-xs">Week Start</Label>
                        <Input type="date" value={dateParam} onChange={e => setDateParam(e.target.value)} max={todayStr} className="h-9 text-sm w-full" data-testid="report-date-input" />
                      </div>
                    )}
                    {selectedReport.param === 'month' && (
                      <div className="flex-1">
                        <Label className="text-[10px] md:text-xs">Month</Label>
                        <Input type="month" value={dateParam} onChange={e => setDateParam(e.target.value)} max={thisMonthStr} className="h-9 text-sm w-full" data-testid="report-month-input" />
                      </div>
                    )}
                    {selectedReport.param === 'quarter' && (
                      <div className="flex-1">
                        <Label className="text-[10px] md:text-xs">Quarter</Label>
                        <select value={dateParam} onChange={e => setDateParam(e.target.value)} className="h-9 text-sm border rounded px-2 w-full" data-testid="report-quarter-select">
                          <option value="">Current</option>
                          {[1,2,3,4].map(q => <option key={q} value={`${new Date().getFullYear()}-Q${q}`}>{new Date().getFullYear()}-Q{q}</option>)}
                        </select>
                      </div>
                    )}
                    {(selectedReport.id === 'slow_moving' || selectedReport.id === 'batch_traceability' || selectedReport.id === 'customer_stock') && (
                      <div className="flex-1">
                        <Label className="text-[10px] md:text-xs">{selectedReport.id === 'slow_moving' ? 'Days Threshold' : selectedReport.id === 'batch_traceability' ? 'Heat Number' : 'Customer Name'}</Label>
                        <Input value={extraParam} onChange={e => setExtraParam(e.target.value)}
                          placeholder={selectedReport.id === 'slow_moving' ? '60' : selectedReport.id === 'batch_traceability' ? 'Enter heat no...' : 'Enter customer...'}
                          className="h-9 text-sm w-full" data-testid="report-extra-param" />
                      </div>
                    )}
                    {selectedReport.hasStockFilters && (
                      <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="stock-filters">
                        <div>
                          <Label className="text-[10px]">Customer</Label>
                          <Input
                            list="customer-list"
                            value={stockFilters.customer_name}
                            onChange={e => setStockFilters(p => ({ ...p, customer_name: e.target.value }))}
                            placeholder="All customers"
                            className="h-9 text-sm"
                            data-testid="filter-customer"
                          />
                          <datalist id="customer-list">
                            {customers.map(c => <option key={c.id} value={c.name} />)}
                          </datalist>
                        </div>
                        <div>
                          <Label className="text-[10px]">Category</Label>
                          <select
                            value={stockFilters.category}
                            onChange={e => setStockFilters(p => ({ ...p, category: e.target.value }))}
                            className="h-9 text-sm w-full border rounded-md px-2 bg-white"
                            data-testid="filter-category"
                          >
                            <option value="">All categories</option>
                            {['Bolt','Nut','Washer','Stud','Screw','Rod','Pin','Other'].map(c => <option key={c} value={c.toLowerCase()}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Item Code contains</Label>
                          <Input
                            value={stockFilters.item_code}
                            onChange={e => setStockFilters(p => ({ ...p, item_code: e.target.value.toUpperCase() }))}
                            placeholder="e.g. M10"
                            className="h-9 text-sm font-mono"
                            data-testid="filter-item-code"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Finish</Label>
                          <Input
                            value={stockFilters.finish}
                            onChange={e => setStockFilters(p => ({ ...p, finish: e.target.value }))}
                            placeholder="e.g. Zinc"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Group by</Label>
                          <select
                            value={stockFilters.group_by}
                            onChange={e => setStockFilters(p => ({ ...p, group_by: e.target.value }))}
                            className="h-9 text-sm w-full border rounded-md px-2 bg-white"
                            data-testid="filter-group-by"
                          >
                            <option value="">(No grouping — flat list)</option>
                            <option value="customer_name">Customer</option>
                            <option value="category">Category</option>
                            <option value="item_code">Item Code</option>
                            <option value="row">Warehouse Row</option>
                          </select>
                        </div>
                        <div className="flex flex-col justify-end">
                          <div className="text-[10px] mb-1">Statuses to include</div>
                          <div className="flex gap-3 text-[11px]">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={stockFilters.include_qc_hold}
                                onChange={e => setStockFilters(p => ({ ...p, include_qc_hold: e.target.checked }))} />
                              QC-Hold
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={stockFilters.include_partial}
                                onChange={e => setStockFilters(p => ({ ...p, include_partial: e.target.checked }))} />
                              Partial
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={stockFilters.include_ready}
                                onChange={e => setStockFilters(p => ({ ...p, include_ready: e.target.checked }))} />
                              Ready
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action buttons — stack on mobile */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button size="sm" onClick={() => fetchReport(selectedReport)} disabled={loading} className="h-9 gap-1 w-full sm:w-auto" data-testid="report-preview-btn">
                      <Eye className="h-3 w-3" /> {loading ? 'Loading...' : 'Preview'}
                    </Button>
                    {selectedReport.hasStockFilters && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleServerDownload('xlsx')} className="h-9 gap-1 w-full sm:w-auto border-[#1a2fce] text-[#1a2fce]" data-testid="report-download-xlsx">
                          <Download className="h-3 w-3" /> Download Excel
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleServerDownload('csv')} className="h-9 gap-1 w-full sm:w-auto" data-testid="report-download-csv">
                          <Download className="h-3 w-3" /> Download CSV
                        </Button>
                      </>
                    )}
                    {reportData && userRole !== 'user' && !selectedReport.hasStockFilters && (
                      <>
                        <Button size="sm" variant="outline" onClick={handleExportExcel} className="h-9 gap-1 w-full sm:w-auto" data-testid="report-export-btn">
                          <Download className="h-3 w-3" /> Export Excel
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleEmailNow} className="h-9 gap-1 w-full sm:w-auto" data-testid="report-email-btn">
                          <Mail className="h-3 w-3" /> Email Now
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Content */}
            {loading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            )}

            {reportData && !loading && (
              <Card data-testid="report-content-card">
                <CardContent className="pt-4 pb-4">
                  <ReportRenderer data={reportData} reportDef={selectedReport} />
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
